import type {
  AgentAccessFlow,
  AgentFlowSummary,
  FlowBranch,
  FlowGovernanceState,
  FlowIdentityType,
} from "@/api/temp/TrustAIExecutiveService";

/**
 * Agent access flows: Identity -> Agent -> MCP/Gateway -> Data Platform -> Data
 * Resource, with governance evidence overlaid on each hop.
 *
 * This is the "why is this happening" surface. The board answers "where is the
 * problem"; a flow answers what the agent actually reaches and which control
 * is missing on the way. That is why every branch carries two states rather
 * than one: the access-layer policy and the data policy fail independently, and
 * a single status could not say the MCP policy is missing while the data policy
 * holds.
 *
 * Only `customerInsightsFlow` is authored in full - it is the one the spec
 * walks through end to end. The rest come from `representativeFlow`, which
 * covers every business unit, both identity types and all four governance
 * states without pretending to be a complete estate. `agentFlowSummary` is the
 * real population; the flows here are a representative sample of it, and the
 * UI says so rather than implying the list is exhaustive.
 */

const RESOURCE_PATHS: Record<string, string[]> = {
  Snowflake: ["CUSTOMER_DB", "CUSTOMER_SCHEMA", "CUSTOMER_PROFILE"],
  Databricks: ["ANALYTICS_CATALOG", "MODEL_FEATURES"],
  Oracle: ["FINANCE", "REPORTING"],
  "SQL Server": ["OPERATIONS", "dbo", "CASES"],
  SharePoint: ["Customer Documents"],
};

/** The spec's worked example, §26–§28. */
const customerInsightsFlow: AgentAccessFlow = {
  id: "customer-insights",
  name: "Customer Insights Agent",
  businessUnit: "Customer Service",
  identity: "svc-customer-01",
  identityType: "SERVICE_ACCOUNT",
  status: "NEEDS_ATTENTION",
  activePolicies: 2,
  policyGaps: 1,
  recentViolations: 1,
  branches: [
    {
      id: "customer-insights-snowflake",
      accessLayer: { label: "Customer MCP Server", detail: "MCP server" },
      dataPlatform: { label: "Customer Analytics", detail: "Snowflake" },
      resourcePath: RESOURCE_PATHS.Snowflake,
      accessState: "POLICY_GAP",
      accessStateDetail:
        "No access policy governs what this agent may invoke through the Customer MCP Server.",
      dataState: "VIOLATION",
      dataStateDetail:
        "Sensitive customer attributes were read beyond the agent's approved purpose.",
    },
    {
      id: "customer-insights-sharepoint",
      accessLayer: { label: "Document MCP Server", detail: "MCP server" },
      dataPlatform: { label: "Customer Documents", detail: "SharePoint" },
      resourcePath: RESOURCE_PATHS.SharePoint,
      accessState: "POLICY_ENFORCED",
      accessStateDetail: "Document access is governed by an enforced policy.",
      dataState: "POLICY_ENFORCED",
      dataStateDetail: "Document classification and retention rules apply.",
    },
  ],
  evidence: [
    {
      id: "customer-insights-agent-policy",
      title: "Agent Data Access Policy",
      state: "POLICY_ENFORCED",
      detail:
        "The agent's baseline data-access policy is enforced at every request.",
    },
    {
      id: "customer-insights-mcp-gap",
      title: "Customer MCP Access Policy",
      state: "POLICY_GAP",
      detail:
        "No policy constrains which tools this agent may invoke through the Customer MCP Server.",
      resource: "Customer MCP Server",
      recommendedPolicy: "Apply least-privilege access policies.",
      controlId: "over-authorized",
    },
    {
      id: "customer-insights-violation",
      title: "Sensitive Data Access Violation",
      state: "VIOLATION",
      detail:
        "Agent accessed sensitive customer attributes beyond intended policy scope.",
      resource: "CUSTOMER_PROFILE",
      recommendedPolicy: "Enforce approved intent and access boundaries.",
      controlId: "agent-behavior-misalignment",
    },
    {
      id: "customer-insights-sharepoint-policy",
      title: "SharePoint Document Policy",
      state: "POLICY_ENFORCED",
      detail: "Document access stays within the approved classification.",
    },
  ],
};

interface RepresentativeFlowInput {
  id: string;
  name: string;
  businessUnit: string;
  identity: string;
  identityType?: FlowIdentityType;
  /** Which governance story this flow tells. */
  issue: "gap" | "violation" | "secure" | "not-evaluated";
  platform?: keyof typeof RESOURCE_PATHS;
  /** Present when the agent reaches a second platform through a second MCP. */
  secondPlatform?: keyof typeof RESOURCE_PATHS;
}

const STATE_DETAIL: Record<FlowGovernanceState, string> = {
  POLICY_ASSIGNED: "Policy assigned; runtime enforcement has not been verified.",
  POLICY_ENFORCED: "Access is governed by an enforced policy.",
  POLICY_GAP: "No policy governs this relationship yet.",
  VIOLATION: "Observed access exceeded the approved policy scope.",
  NOT_EVALUATED: "This relationship has not been evaluated yet.",
};

const branch = (
  id: string,
  accessLayer: string,
  platform: keyof typeof RESOURCE_PATHS,
  accessState: FlowGovernanceState,
  dataState: FlowGovernanceState,
): FlowBranch => ({
  id,
  accessLayer: { label: accessLayer, detail: "MCP server" },
  dataPlatform: { label: `${platform} workspace`, detail: platform },
  resourcePath: RESOURCE_PATHS[platform],
  accessState,
  accessStateDetail: STATE_DETAIL[accessState],
  dataState,
  dataStateDetail: STATE_DETAIL[dataState],
});

const ISSUE_STATES: Record<
  RepresentativeFlowInput["issue"],
  {
    status: AgentAccessFlow["status"];
    access: FlowGovernanceState;
    data: FlowGovernanceState;
    gaps: number;
    violations: number;
  }
> = {
  gap: {
    status: "NEEDS_ATTENTION",
    access: "POLICY_GAP",
    data: "POLICY_ENFORCED",
    gaps: 1,
    violations: 0,
  },
  violation: {
    status: "NEEDS_ATTENTION",
    access: "POLICY_ENFORCED",
    data: "VIOLATION",
    gaps: 0,
    violations: 1,
  },
  secure: {
    status: "SECURE",
    access: "POLICY_ENFORCED",
    data: "POLICY_ENFORCED",
    gaps: 0,
    violations: 0,
  },
  "not-evaluated": {
    status: "NOT_EVALUATED",
    access: "NOT_EVALUATED",
    data: "NOT_EVALUATED",
    gaps: 0,
    violations: 0,
  },
};

const representativeFlow = ({
  id,
  name,
  businessUnit,
  identity,
  identityType = "SERVICE_ACCOUNT",
  issue,
  platform = "Snowflake",
  secondPlatform,
}: RepresentativeFlowInput): AgentAccessFlow => {
  const states = ISSUE_STATES[issue];
  const branches: FlowBranch[] = [
    branch(
      `${id}-primary`,
      `${businessUnit} MCP Server`,
      platform,
      states.access,
      states.data,
    ),
  ];
  if (secondPlatform) {
    branches.push(
      branch(
        `${id}-secondary`,
        "Document MCP Server",
        secondPlatform,
        "POLICY_ENFORCED",
        "POLICY_ENFORCED",
      ),
    );
  }

  return {
    id,
    name,
    businessUnit,
    identity,
    identityType,
    status: states.status,
    activePolicies: branches.length * 2 - states.gaps - states.violations,
    policyGaps: states.gaps,
    recentViolations: states.violations,
    branches,
    evidence: [
      {
        id: `${id}-agent-policy`,
        title: "Agent Data Access Policy",
        state: issue === "not-evaluated" ? "NOT_EVALUATED" : "POLICY_ENFORCED",
        detail:
          issue === "not-evaluated"
            ? "Classification has not reached this agent yet."
            : "The agent's baseline data-access policy is enforced.",
      },
      ...(states.gaps
        ? [
            {
              id: `${id}-gap`,
              title: `${businessUnit} MCP Access Policy`,
              state: "POLICY_GAP" as const,
              detail:
                "No policy constrains which tools this agent may invoke through its MCP server.",
              resource: `${businessUnit} MCP Server`,
              recommendedPolicy: "Apply least-privilege access policies.",
              controlId: "over-authorized",
            },
          ]
        : []),
      ...(states.violations
        ? [
            {
              id: `${id}-violation`,
              title: "Sensitive Data Access Violation",
              state: "VIOLATION" as const,
              detail:
                "Observed access exceeded the agent's approved intent and data boundary.",
              resource: RESOURCE_PATHS[platform].at(-1),
              recommendedPolicy:
                "Enforce approved intent and access boundaries.",
              controlId: "agent-behavior-misalignment",
            },
          ]
        : []),
    ],
  };
};

export const agentAccessFlows: AgentAccessFlow[] = [
  customerInsightsFlow,
  representativeFlow({
    id: "research-assistant",
    name: "Research Assistant",
    businessUnit: "Customer Service",
    identity: "svc-research-02",
    issue: "gap",
    secondPlatform: "SharePoint",
  }),
  representativeFlow({
    id: "case-summarizer",
    name: "Case Summarizer",
    businessUnit: "Customer Service",
    identity: "mi-case-03",
    identityType: "MANAGED_IDENTITY",
    issue: "not-evaluated",
    platform: "SQL Server",
  }),
  representativeFlow({
    id: "finance-research",
    name: "Finance Research Agent",
    businessUnit: "Finance",
    identity: "svc-finance-04",
    issue: "gap",
    platform: "Oracle",
  }),
  representativeFlow({
    id: "finance-analysis",
    name: "Finance Analysis Agent",
    businessUnit: "Finance",
    identity: "svc-analysis-05",
    issue: "violation",
    platform: "Databricks",
  }),
  representativeFlow({
    id: "expense-review",
    name: "Expense Review Agent",
    businessUnit: "Finance",
    identity: "mi-expense-06",
    identityType: "MANAGED_IDENTITY",
    issue: "secure",
    platform: "Oracle",
  }),
  representativeFlow({
    id: "hr-assistant",
    name: "HR Assistant",
    businessUnit: "Human Resources",
    identity: "svc-hr-07",
    issue: "violation",
    secondPlatform: "SharePoint",
  }),
  representativeFlow({
    id: "candidate-screening",
    name: "Candidate Screening Assistant",
    businessUnit: "Human Resources",
    identity: "svc-candidate-08",
    issue: "gap",
    platform: "Databricks",
  }),
  representativeFlow({
    id: "engineering-copilot",
    name: "Engineering Copilot",
    businessUnit: "Engineering",
    identity: "mi-engineering-09",
    identityType: "MANAGED_IDENTITY",
    issue: "secure",
    platform: "Databricks",
  }),
  representativeFlow({
    id: "release-auditor",
    name: "Release Auditor",
    businessUnit: "Engineering",
    identity: "svc-release-10",
    issue: "gap",
    platform: "SQL Server",
  }),
  representativeFlow({
    id: "insights-explorer",
    name: "Insights Explorer",
    businessUnit: "Analytics",
    identity: "svc-insights-11",
    issue: "violation",
    platform: "Snowflake",
  }),
  representativeFlow({
    id: "market-research",
    name: "Market Research Assistant",
    businessUnit: "Analytics",
    identity: "mi-market-12",
    identityType: "MANAGED_IDENTITY",
    issue: "not-evaluated",
    platform: "SQL Server",
  }),
];

/**
 * Estate-wide counts, deliberately larger than the sample above: the flows are
 * representative, the summary is the population. The flows view reconciles the
 * two in copy rather than implying twelve agents are the whole estate.
 */
export const agentFlowSummary: Omit<
  AgentFlowSummary,
  "businessUnits" | "dataPlatforms"
> = {
  agentsNeedingAttention: 51,
  serviceAccountsToReview: 14,
  pathsWithPolicyGaps: 23,
};

/**
 * The agents behind the two findings that carry relationship evidence.
 *
 * Exported so `seed.ts` can attach them to those controls' `flowContext`
 * without restating the ids, which is what keeps "View Access Flow" and the
 * list it opens describing the same set of agents.
 */
export const CROSS_BORDER_FLOW_AGENT_IDS = [
  "customer-insights",
  "research-assistant",
  "finance-research",
  "finance-analysis",
  "hr-assistant",
  "insights-explorer",
  "candidate-screening",
  "release-auditor",
  "case-summarizer",
];

export const OVER_AUTHORIZED_FLOW_AGENT_IDS = [
  "customer-insights",
  "research-assistant",
  "finance-research",
  "candidate-screening",
  "release-auditor",
  "hr-assistant",
];
